import AnimalIcon from 'Components/AnimalIcon';
import { OutlineButton, PrimaryButton } from 'Components/Buttons';
import { Box, Text } from 'grommet';
import { memo } from 'preact/compat';
import { useEffect, useState } from 'preact/hooks';
import { useDispatch } from 'react-redux';
import { buildURL } from 'Shared/fetch';
import useFetch from 'use-http';

import EditVideoModal from '@/components/EditVideoModal';
import { setShareModalData } from '@/components/ShareModal/shareModalSlice/actions';
import habitatIdOfMedia from '@/queries/media/habitat';
import { useSelector } from '@/redux/helper';
import { updateAlbumData } from '@/redux/slices/myAlbum/actions';
import { useCan } from '@/shared/entitlements';

import { formatTimeAgoOrTimestamp } from '../../../helpers';
import { updateHabitatAlbumData } from '../../../routes/habitat/components/Album/actions';
import style from './style.scss';

/**
 * @param {object} props
 * @param {string} props.mediaId
 * @param {object} props.data
 * @param {number} [props.maxLength]
 * @param {(string) => void} [props.onUpdate]
 * @param {boolean} [props.editInPopup]
 * @returns
 */
const EditTitle = ({ mediaId, data, maxLength = 100, onUpdate, editInPopup = false }) => {
  const userId = useSelector((state) => state.user.userId);
  const can = useCan();
  const mediaHabitatId = habitatIdOfMedia(data);
  const canEditDetails = can('media.editDetails', mediaHabitatId);
  const canEditAnyCaption = can('media.editAnyCaption', mediaHabitatId);
  const shareModalData = useSelector((state) => state.habitat.shareModal);
  // TODO unify data and shareModalData, not all modals use redux some are standalone modals
  const { title = '', userId: ownerId, profile, username } = data ?? {};

  const dispatch = useDispatch();

  const [editMode, setEditMode] = useState(false);
  const [text, setText] = useState(title);

  const { post, response, error, loading } = useFetch('', {
    credentials: 'include',
    cachePolicy: 'no-cache',
    headers: { 'Content-Type': 'application/json' },
  });

  useEffect(() => {
    setText(title);
    setEditMode(false);
  }, [title]);

  const submitHandler = async () => {
    await post(buildURL(`/media/${mediaId}`), { title: text });

    if (response?.ok && response?.data?.media && !error) {
      // update data in modal
      dispatch(setShareModalData({ ...shareModalData, data: response.data.media }));
      // update data in album card inside habitat and my album
      const payload = { id: mediaId, data: response.data.media };
      dispatch(updateHabitatAlbumData(payload));
      dispatch(updateAlbumData(payload));
      onUpdate?.(text);
    }

    setEditMode(false);
  };

  return (
    <div id={mediaId} className={style.editTitleContainer}>
      <div className={style.user}>
        <a className="preflight preflight-a" href={`/u/${username}`}>
          <AnimalIcon width={30} animalIcon={profile?.animal || profile?.animalIcon} color={profile?.color} />
        </a>
        <div className={style.userName}>
          <a className="preflight preflight-a font-semibold" href={`/u/${username}`}>
            <span>{username}</span>
          </a>
          <span className={style.time}>{`${formatTimeAgoOrTimestamp(
            new Date(data.publishedAt || data.createdAt || data.creationDate),
          )}`}</span>
        </div>
      </div>

      <div className={style.titleContainer}>
        {!editMode && (
          <>
            <span className={style.title}>{title}</span>
            {editInPopup && canEditDetails ? (
              <EditVideoModal.Trigger
                videoId={mediaId}
                defaultThumbnailURL={data?.thumbnailURL}
                defaultTitle={title}
                defaultPublishedAt={data?.publishedAt}
                canEditPlaylists={!data?.disabled}
                onUpdate={(media) => onUpdate?.(media.title)}
                render={(setIsOpen) => (
                  <button className={style.editButton} type="button" onClick={() => setIsOpen(true)}>
                    Edit
                  </button>
                )}
              />
            ) : (
              (userId === ownerId || canEditAnyCaption) && (
                <button className={style.editButton} type="button" onClick={() => setEditMode(!editMode)}>
                  Edit
                </button>
              )
            )}
          </>
        )}

        {editMode && (
          <>
            <Box className={style.textarea}>
              <textarea
                placeholder="Type your description"
                value={text}
                onChange={(e) => setText(e.target.value)}
                maxLength={maxLength}
              />
              <span className={style.letterCount} style={{ color: text.length === maxLength ? 'var(--red)' : 'unset' }}>
                {`${text.length}/${maxLength}`}
              </span>
            </Box>
            {error && (
              <Box align="center" margin={{ top: '20px' }}>
                <Text color="red">Try again!</Text>
              </Box>
            )}

            <Box margin={{ vertical: '15px', horizontal: '10px' }} direction="row" justify="end">
              <OutlineButton
                style={{ minWidth: '75px', marginRight: '10px' }}
                label="Cancel"
                size="small"
                onClick={() => setEditMode(false)}
                justify="center"
              />
              <PrimaryButton
                style={{ minWidth: '75px' }}
                label="Save"
                size="small"
                loading={loading}
                disabled={loading || text.length === 0 || text.length > maxLength}
                onClick={submitHandler}
                justify="center"
              />
            </Box>
          </>
        )}
      </div>
    </div>
  );
};

export default memo(EditTitle);
